import axios, { AxiosRequestConfig, Method } from 'axios';
import { mapValues, merge } from 'lodash';
import { from, map } from 'rxjs';
import { encodeQuery } from 'lib/transport';
import { QueryParams } from 'types/transport';

/**
 * Update default Axios configuration from the specified application configuration object
 *
 * @param {string} baseUrl API base URL
 */
export const configureAxios = (baseUrl?: string) => {
  axios.defaults.baseURL = baseUrl;
  axios.defaults.timeout = 120000;

  axios.defaults.headers.common['Accept'] = 'application/json';
  axios.defaults.headers.common['Content-Type'] = 'application/json';
};

/**
 * Create a new request with the specified parameters
 *
 * @param {Method} method Target HTTP method
 * @param {string} url Request URL
 * @param {any} data Request payload
 * @param {Array<AxiosRequestConfig>} config Custom configuration overrides
 */
const createRequest = async <TResponse>(
  method: Method,
  url: string,
  data: any,
  ...config: Array<AxiosRequestConfig>
) =>
  await axios.request<TResponse>({
    ...merge({}, ...config),
    data,
    method,
    url,
  } as AxiosRequestConfig);

/**
 * Create request authorization headers
 *
 * @param {string} token Access token
 */
export const createAuthorizationHeaders = (token?: string): AxiosRequestConfig =>
  token ? { headers: { Authorization: `Bearer ${token}` } } : {};

/**
 * Create request query parameter configuration
 */
export const createRequestQuery = <TQuery extends QueryParams>(
  params?: TQuery,
): AxiosRequestConfig => ({
  params: mapValues(params ?? {}, value => value ?? null),
  paramsSerializer: (query: QueryParams) => encodeQuery(query, 'repeat') ?? '',
});

/**
 * Create an observable emitting the response of a request
 *
 * @param {Method} method Target HTTP method
 * @param {string} url Request URL
 * @param {any} data Request payload
 * @param {Array<AxiosRequestConfig>} config Custom configuration overrides
 */
export const createRequest$ = <TResponse>(
  method: Method,
  url: string,
  data: any,
  ...config: Array<AxiosRequestConfig>
) =>
  from(createRequest<TResponse>(method, url, data, ...config)).pipe(map(response => response.data));

/**
 * Create an observable emitting response of a GET request
 *
 * @param {string} url Request URL
 * @param {Array<AxiosRequestConfig>} config Custom configuration overrides
 */
export const get$ = <TResponse>(url: string, ...config: Array<AxiosRequestConfig>) =>
  createRequest$<TResponse>('GET', url, undefined, ...config);

/**
 * Create an observable emitting response of a DELETE request
 *
 * @param {string} url Request URL
 * @param {*} data Request payload
 * @param {Array<AxiosRequestConfig>} config Custom configuration overrides
 */
export const delete$ = <TResponse>(url: string, data: any, ...config: Array<AxiosRequestConfig>) =>
  createRequest$<TResponse>('DELETE', url, data, ...config);

/**
 * Create an observable emitting response of a POST request
 *
 * @param {string} url Request URL
 * @param {*} data Request payload
 * @param {Array<AxiosRequestConfig>} config Custom configuration overrides
 */
export const post$ = <TResponse, TPayload = any>(
  url: string,
  data: TPayload,
  ...config: Array<AxiosRequestConfig>
) => createRequest$<TResponse>('POST', url, data, ...config);

/**
 * Create an observable emitting response of a PUT request
 *
 * @param {string} url Request URL
 * @param {any} data Request payload
 * @param {Array<AxiosRequestConfig>} config Custom configuration overrides
 */
export const put$ = <TResponse>(url: string, data: any, ...config: Array<AxiosRequestConfig>) =>
  createRequest$<TResponse>('PUT', url, data, ...config);
